// Paging, filtering and searching the dashboard's request list. Shared by the list endpoint and
// the dashboard so both agree on what a page is; import-free so plain Node can load it.

export type StatusFilter = "all" | "pending" | "confirmed" | "expired";
export const STATUS_FILTERS: readonly StatusFilter[] = ["all", "pending", "confirmed", "expired"];

/** Rows per page the dashboard offers. Ten fits about one screen. */
export const PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_PAGE_SIZE = 10;

const MAX_QUERY = 100;
const MAX_PAGE = 100_000;

export interface ListParams {
  status: StatusFilter;
  /** What the user typed in the search box, cleaned. Empty means no search. */
  q: string;
  page: number;
  pageSize: number;
}

/** The terms a search looks in, and what the list endpoint accepts. Anything
 *  unrecognised falls back to the default rather than failing the request. */
export function parseListParams(sp: URLSearchParams): ListParams {
  const s = sp.get("status") ?? "all";
  const status = (STATUS_FILTERS as readonly string[]).includes(s) ? (s as StatusFilter) : "all";

  const rawPage = Number(sp.get("page"));
  const page = Number.isInteger(rawPage) && rawPage >= 1 ? Math.min(rawPage, MAX_PAGE) : 1;

  const rawSize = Number(sp.get("pageSize"));
  const pageSize = (PAGE_SIZES as readonly number[]).includes(rawSize) ? rawSize : DEFAULT_PAGE_SIZE;

  return { status, q: searchTerm(sp.get("q") ?? ""), page, pageSize };
}

/** The search text with control characters and `*` (PostgREST's wildcard)
 *  taken out, spaces collapsed, and a sensible length. */
export function searchTerm(q: string): string {
  let out = "";
  for (const ch of q) {
    const c = ch.codePointAt(0)!;
    out += c < 0x20 || c === 0x7f || ch === "*" ? " " : ch;
  }
  return Array.from(out.replace(/\s+/g, " ").trim()).slice(0, MAX_QUERY).join("");
}

/** Makes `%`, `_` and `\` match themselves in a LIKE pattern, so searching for
 *  "50%" finds "50%" and not everything that starts with 50. */
function likeEscape(s: string): string {
  return s.replace(/[\\%_]/g, "\\$&");
}

/** Wraps a value in double quotes for PostgREST's filter syntax, so commas and
 *  parentheses in it can't be read as part of the filter. */
function quote(s: string): string {
  return `"${s.replace(/[\\"]/g, "\\$&")}"`;
}

/** The value of PostgREST's `or=` filter for "contains this text, any case, in
 *  the reference, description, payer name or payer email", or null when there
 *  is nothing to search for. The caller URL-encodes it. */
export function searchFilter(q: string): string | null {
  const term = searchTerm(q);
  if (!term) return null;
  const pattern = quote(`*${likeEscape(term)}*`);
  const columns = ["reference", "description", "payer_name", "payer_email"];
  return `(${columns.map((c) => `${c}.ilike.${pattern}`).join(",")})`;
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * The buttons in the pager: every page up to seven, otherwise the first, the
 * last and a window around the current one, with "gap" for what is skipped.
 * Always seven items when there are more than seven pages, so the pager keeps
 * the same width as you move through it.
 */
export function pageItems(current: number, pages: number): (number | "gap")[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "gap", pages];
  if (current >= pages - 3) return [1, "gap", pages - 4, pages - 3, pages - 2, pages - 1, pages];
  return [1, "gap", current - 1, current, current + 1, "gap", pages];
}

/** "26–50 of 536", with thousands separators: "1,251–1,260 of 1,268". */
export function rangeText(page: number, pageSize: number, total: number): string {
  if (total === 0) return "0 of 0";
  const n = (x: number) => x.toLocaleString("en-US");
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return `${n(start)}–${n(end)} of ${n(total)}`;
}
